import {
  requestConfirmShopLiveAlarmTalk,
  reuestRegisterShopLiveAlarmTalk,
  reuestUnregisterShopLiveAlarmTalk,
} from '@apis/renewal/shoplive';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { ShopLiveListResponse } from '@wconcept/wconcept-common/models/renewal/shoplive';
import { CommonResponse } from '@wconcept/wconcept-common/models/common-response';
import { setCookieWckLiveAlarmValue } from '@wconcept/wconcept-common/utils/cookie';
import {
  addAsIsMWebDomain,
  currentUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import useGlobalState from './useGlobalState';
import useHistoryRouter from './useLinkTo';

interface RegisterAlarmParams {
  campaignKey: string;
}

interface UseLiveAlarmOptions {
  onSuccess?: (data: CommonResponse<boolean | ShopLiveListResponse>) => void;
  onError?: (error: unknown) => void;
}

const useLiveAlarm = (options?: UseLiveAlarmOptions) => {
  const { assistData, appInfo } = useGlobalState();
  const { linkTo } = useHistoryRouter();

  const registerAlarmMutation = useMutation<
    CommonResponse<ShopLiveListResponse>,
    unknown,
    RegisterAlarmParams
  >({
    mutationFn: reuestRegisterShopLiveAlarmTalk,
    onSuccess: res => {
      if (res.result === 'SUCCESS') {
        if (assistData.user.liveAlarmYN !== 'Y') {
          setCookieWckLiveAlarmValue('Y');
        }
        alert(`알림이 신청 되었습니다. 라이브 시작 시 알림톡이 발송됩니다.`);
        options?.onSuccess?.(res);
      }
      if (res.result === 'FAIL') {
        options?.onError?.(res);
      }
    },
    onError: error => {
      console.error('Failed to register alarm', error);
      options?.onError?.(error);
    },
  });

  const unregisterAlarmMutation = useMutation<
    CommonResponse<boolean>,
    unknown,
    RegisterAlarmParams & { successText?: string }
  >({
    mutationFn: reuestUnregisterShopLiveAlarmTalk,
    onSuccess: (res, variables) => {
      if (res.result === 'SUCCESS') {
        alert(variables.successText || `알림이 신청 취소되었습니다.`);
        options?.onSuccess?.(res);
      }
      if (res.result === 'FAIL') {
        options?.onSuccess?.(res);
      }
    },
    onError: error => {
      console.error('Failed to register alarm', error);
      options?.onError?.(error);
    },
  });

  const registerAlarm = async (
    params: RegisterAlarmParams
  ): Promise<CommonResponse<ShopLiveListResponse> | undefined> => {
    if (!assistData.isLogin) {
      if (
        window.confirm(
          '로그인 페이지로 이동합니다. 로그인 완료 후, 알림신청 버튼을 다시 클릭해 주세요.'
        )
      ) {
        const targetUrl = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;
        linkTo.push({
          url: targetUrl,
        });
      }

      return undefined;
    }
    return registerAlarmMutation.mutateAsync(params);
  };

  const unregisterAlarm = async (
    params: RegisterAlarmParams,
    text?: string,
    successText?: string
  ) => {
    const confirmText = text || '알림 신청을 취소할까요?';
    if (window.confirm(confirmText)) {
      return unregisterAlarmMutation.mutateAsync({ ...params, successText });
    }
    return undefined;
  };

  const confirmAlarm = async () => requestConfirmShopLiveAlarmTalk();

  return {
    registerAlarm,
    unregisterAlarm,
    confirmAlarm,
    ...registerAlarmMutation,
  };
};

export default useLiveAlarm;
